import {
    ActionIcon,
    Button,
    Chip,
    Divider,
    FileInput,
    Group,
    Modal,
    SegmentedControl,
    Select,
    Slider,
    Switch,
    Tabs,
    Text,
} from "@mantine/core";
import React, { useState } from "react";
import { BoardColor, GameDuration, Settings, TimeControl, User } from "./models";
import { IconInfoTriangle, IconLock, IconTrash } from "@tabler/icons-react";
import cn from "classnames";
import { getCanWinTrophies } from "./utils";
import { useSettingsStorage, useStorage } from "./ZustandState";
import InfoPopover from "./InfoPopover";
import { useMediaQuery } from "@mantine/hooks";

interface Props {
    settings: Settings;
    user?: User;
}

const SettingsModal: React.FC<Props> = ({ settings, user }) => {
    // user = {
    //     isPremium: false,
    // };

    const openedSettings = useStorage((state) => state.openedSettings);
    const setOpenedSettings = useStorage((state) => state.setOpenedSettings);
    const canWinTrophies = getCanWinTrophies(settings);
    const setSettings = useSettingsStorage((state) => state.setSettings);
    const levels = useStorage((state) => state.levels);
    const setLevels = useStorage((state) => state.setLevels);

    const [startRating, setStartRating] = useState(settings.startRating);
    const [gameDuration, setGameDuration] = useState(settings.gameDuration);

    const handleResetToDefault = () => {
        setStartRating(1200);
        setGameDuration("medium");
        setSettings({
            timeControls: ["blitz", "rapid"],
            showEval: true,
            startFromInitial: false,
            rejectionThreshold: 0,
            preferDubious: false,
            startRating: 1200,
            gameDuration: "long",
            allowMultipleLevels: false,
            engineSource: "lichess",
            isRated: true,
            lichessPlayer: undefined,
            lichessPlayerSettings: {
                sinceYear: "2010",
                sinceMonth: "January",
                untilYear: "2025",
                untilMonth: "December",
            },
            boardColor: "brown",
            whiteRepo: undefined,
            blackRepo: undefined,
            mistakeMode: "all",
            showCapturedPieces: true,
            useLocalStockfish: false,
            stockfishDepth2: "Auto",
        });
    };

    const isMobile = useMediaQuery("(max-width: 1024px)");

    return (
        <Modal
            opened={openedSettings}
            onClose={() => setOpenedSettings(false)}
            title="Settings"
            padding={30}
            size="30rem"
        >
            <Tabs defaultValue="general">
                <Tabs.List>
                    <Tabs.Tab value="general">General</Tabs.Tab>
                    {/* <Tabs.Tab value="rated">Rated mode</Tabs.Tab> */}
                    <Tabs.Tab value="training">Training mode</Tabs.Tab>
                    <Tabs.Tab value="repo">Advanced</Tabs.Tab>
                    {/* <Tabs.Tab value="experimental">Experimental</Tabs.Tab> */}
                </Tabs.List>

                <Tabs.Panel pt="xl" value="general">
                    <div className={cn("flex items-center")}>
                        {/* {!user?.isPremium && <IconLock />} */}
                        <div>
                            <Text fz="sm">Game duration</Text>
                            <SegmentedControl
                                w="18rem"
                                // disabled={!user?.isPremium}
                                size="xs"
                                styles={{
                                    root: {
                                        backgroundColor: "var(--mantine-color-dark-5)",
                                    },
                                }}
                                color="blue"
                                value={gameDuration}
                                onChange={(value) => {
                                    setGameDuration(value as GameDuration);
                                }}
                                data={[
                                    {
                                        value: "short",
                                        label: "Short",
                                    },
                                    {
                                        value: "medium",
                                        label: "Medium",
                                    },
                                    {
                                        value: "long",
                                        label: "Long",
                                    },
                                    {
                                        value: "veryLong",
                                        label: "Very long",
                                    },
                                    {
                                        value: "max",
                                        label: "Max",
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    {gameDuration === "max" && (
                        <Text c="dimmed" fz="xs">
                            With Max selected it will play until a unique position has been reached
                        </Text>
                    )}

                    <Select
                        className="ml-auto"
                        mt="xl"
                        label="Board color"
                        value={settings.boardColor}
                        data={[
                            { value: "brown", label: "Brown" },
                            { value: "green", label: "Green" },
                            { value: "blue", label: "Blue" },
                        ]}
                        allowDeselect={false}
                        onChange={(option) => {
                            setSettings({ ...settings, boardColor: option as BoardColor });
                        }}
                    />

                    <div className={cn("flex items-center mt-10", { "space-x-2 opacity-70": !user?.isPremium })}>
                        {!user?.isPremium && <IconLock />}
                        <Switch
                            label="Start custom positions from initial position"
                            checked={settings.startFromInitial}
                            disabled={!user?.isPremium}
                            onChange={(e) =>
                                setSettings({
                                    ...settings,
                                    startFromInitial: e.currentTarget.checked,
                                })
                            }
                        />
                    </div>

                    <div className="mt-6">
                        <Switch
                            label="Show captured pieces"
                            checked={settings.showCapturedPieces}
                            disabled={!user?.isPremium}
                            onChange={(e) =>
                                setSettings({
                                    ...settings,
                                    showCapturedPieces: e.currentTarget.checked,
                                })
                            }
                        />
                    </div>

                    <Select
                        className="ml-auto"
                        mt="xl"
                        label="Show mistakes after a game"
                        value={settings.mistakeMode}
                        data={[
                            {
                                value: "all",
                                label: "All",
                            },
                            {
                                value: "mistakes",
                                label: "Only show bigger mistakes",
                            },
                            {
                                value: "none",
                                label: "Don't show mistakes",
                            },
                        ]}
                        allowDeselect={false}
                        onChange={(option) => {
                            setSettings({ ...settings, mistakeMode: option as typeof settings.mistakeMode });
                        }}
                    />
                </Tabs.Panel>

                {/* <Tabs.Panel pt="xl" value="rated">
                </Tabs.Panel> */}

                <Tabs.Panel pt="xl" value="training">
                    <div className="flex items-center">
                        <Switch
                            label="Show eval"
                            checked={settings.showEval}
                            onChange={(e) =>
                                setSettings({
                                    ...settings,
                                    showEval: e.currentTarget.checked,
                                })
                            }
                        />
                    </div>
                    <div className={cn("flex items-center mt-6", { "space-x-2 opacity-70": !user?.isPremium })}>
                        {!user?.isPremium && <IconLock />}
                        <Switch
                            label="Engine prefers trappy/dubious lines"
                            checked={settings.preferDubious}
                            disabled={!user?.isPremium}
                            onChange={(e) =>
                                setSettings({
                                    ...settings,
                                    preferDubious: e.currentTarget.checked,
                                })
                            }
                        />
                    </div>

                    <div className={cn("flex items-center mt-6", { "space-x-2 opacity-70": !user?.isPremium })}>
                        {!user?.isPremium && <IconLock />}
                        <Switch
                            label="Allow selecting multiple rating levels"
                            checked={settings.allowMultipleLevels}
                            disabled={!user?.isPremium}
                            onChange={(e) =>
                                setSettings({
                                    ...settings,
                                    allowMultipleLevels: e.currentTarget.checked,
                                })
                            }
                        />
                    </div>

                    <div className={cn("mt-8", { "opacity-70": !user?.isPremium })}>
                        <div className="flex space-x-2">
                            {!user?.isPremium && <IconLock />}
                            <Text size="sm" c={user?.isPremium ? "" : "#5c5f66"}>
                                Engine only picks moves played at least this % of the time
                            </Text>
                        </div>
                        <div className="flex items-center mt-2 ml-8">
                            <div className="w-full">
                                <Slider
                                    label={(value) => `${value} %`}
                                    value={settings.rejectionThreshold}
                                    disabled={!user?.isPremium}
                                    onChange={(value) =>
                                        setSettings({
                                            ...settings,
                                            rejectionThreshold: value,
                                        })
                                    }
                                    marks={[
                                        { value: 20, label: "20%" },
                                        { value: 50, label: "50%" },
                                        { value: 80, label: "80%" },
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </Tabs.Panel>

                <Tabs.Panel pt="xl" value="repo">
                    <Switch
                        mb="xl"
                        label="Display Stockfish info after games"
                        checked={settings.displayStockfishInfo}
                        onChange={(e) =>
                            setSettings({
                                ...settings,
                                displayStockfishInfo: e.currentTarget.checked,
                            })
                        }
                    />

                    <Select
                        mb="xl"
                        className="ml-auto"
                        label="Stockfish depth"
                        value={settings.stockfishDepth2.toString()}
                        data={["Auto", "18", "19", "20", "21", "22", "23", "24", "25"]}
                        allowDeselect={false}
                        onChange={(option) => {
                            setSettings({ ...settings, stockfishDepth2: option === "Auto" ? "Auto" : Number(option) });
                        }}
                    />

                    {/* <div className={cn("flex items-center", { "space-x-2 opacity-70": !user?.isPremium })}>
                        {!user?.isPremium && <IconLock />}

                        <Radio.Group
                            value={settings.engineSource}
                            onChange={(value) => {
                                setSettings({
                                    ...settings,
                                    engineSource: value as EngineSource,
                                });
                            }}
                            name="favoriteFramework"
                            label="Select engine source"
                        >
                            <Stack mt="xs">
                                <Radio value="lichess" label="Lichess database" disabled={!user?.isPremium} />
                                <Radio
                                    value="lichess-player"
                                    label="Train against a specific player"
                                    disabled={!user?.isPremium}
                                />
                            </Stack>
                        </Radio.Group>
                    </div> */}

                    {/* {settings.engineSource === "lichess-player" && (
                        <TextInput
                            mx="xl"
                            mt="sm"
                            label="Lichess username"
                            value={settings.lichessPlayer}
                            onChange={(e) => setSettings({ ...settings, lichessPlayer: e.target.value })}
                        />
                    )} */}
                    {/* {settings.engineSource === "lichess-player" && (
                        <>
                            <Text ml="xl" mt="lg">
                                Since
                            </Text>
                            <Group justify="left" ml="xl">
                                <Select
                                    w="6rem"
                                    data={[
                                        "2010",
                                        "2011",
                                        "2012",
                                        "2013",
                                        "2014",
                                        "2015",
                                        "2016",
                                        "2017",
                                        "2018",
                                        "2019",
                                        "2020",
                                        "2021",
                                        "2022",
                                        "2024",
                                        "2025",
                                    ]}
                                    value={settings.lichessPlayerSettings.sinceYear}
                                    onChange={(value) =>
                                        setSettings({
                                            ...settings,
                                            lichessPlayerSettings: {
                                                ...settings.lichessPlayerSettings,
                                                sinceYear: value as string,
                                            },
                                        })
                                    }
                                />
                                <Select
                                    w="fit"
                                    data={[
                                        "January",
                                        "February",
                                        "March",
                                        "April",
                                        "May",
                                        "June",
                                        "July",
                                        "August",
                                        "September",
                                        "October",
                                        "November",
                                        "December",
                                    ]}
                                    value={settings.lichessPlayerSettings.sinceMonth}
                                    onChange={(value) =>
                                        setSettings({
                                            ...settings,
                                            lichessPlayerSettings: {
                                                ...settings.lichessPlayerSettings,
                                                sinceMonth: value as string,
                                            },
                                        })
                                    }
                                />
                            </Group>
                        </>
                    )} */}
                    {/* {settings.engineSource === "lichess-player" && (
                        <>
                            <Text ml="xl" mt="lg">
                                Until
                            </Text>
                            <Group justify="left" ml="xl">
                                <Select
                                    w="6rem"
                                    data={[
                                        "2010",
                                        "2011",
                                        "2012",
                                        "2013",
                                        "2014",
                                        "2015",
                                        "2016",
                                        "2017",
                                        "2018",
                                        "2019",
                                        "2020",
                                        "2021",
                                        "2022",
                                        "2024",
                                        "2025",
                                    ]}
                                    value={settings.lichessPlayerSettings.untilYear}
                                    onChange={(value) =>
                                        setSettings({
                                            ...settings,
                                            lichessPlayerSettings: {
                                                ...settings.lichessPlayerSettings,
                                                untilYear: value as string,
                                            },
                                        })
                                    }
                                />
                                <Select
                                    w="fit"
                                    data={[
                                        "January",
                                        "February",
                                        "March",
                                        "April",
                                        "May",
                                        "June",
                                        "July",
                                        "August",
                                        "September",
                                        "October",
                                        "November",
                                        "December",
                                    ]}
                                    value={settings.lichessPlayerSettings.untilMonth}
                                    onChange={(value) =>
                                        setSettings({
                                            ...settings,
                                            lichessPlayerSettings: {
                                                ...settings.lichessPlayerSettings,
                                                untilMonth: value as string,
                                            },
                                        })
                                    }
                                />
                            </Group>
                        </>
                    )} */}
                    {/* <Space h="xl" /> */}

                    {/* <Divider my="lg" /> */}

                    <Group gap="xs" align="center" mb="xs">
                        <Text fz="sm">Time controls</Text>
                        <InfoPopover text="These are the time controls of the games in Lichess which the engine uses to base its moves of. It is not recommended turning off blitz as the vast majorty of games are blitz." />
                    </Group>
                    <Chip.Group
                        multiple={true}
                        value={settings.timeControls}
                        onChange={(value) => {
                            setSettings({
                                ...settings,
                                timeControls: value as TimeControl[],
                            });
                        }}
                    >
                        <Group justify="center">
                            <Chip value="ultraBullet" disabled={!user?.isPremium}>
                                Ultrabullet
                            </Chip>
                            <Chip value="bullet" disabled={!user?.isPremium}>
                                Bullet
                            </Chip>
                            <Chip value="blitz" disabled={!user?.isPremium}>
                                Blitz
                            </Chip>
                        </Group>
                        <Group justify="center" mt="md">
                            <Chip value="rapid" disabled={!user?.isPremium}>
                                Rapid
                            </Chip>
                            <Chip value="classical" disabled={!user?.isPremium}>
                                Classical
                            </Chip>
                        </Group>
                    </Chip.Group>

                    {!isMobile && <Divider my="xl" />}
                    {!isMobile && (
                        <div>
                            <Text mt="lg" size="sm" c="dimmed">
                                Upload your repo as a PGN file. OpeningTrainer will then let you know whether moves
                                played are part of your repo (marked in green) or not.
                            </Text>
                            <div className={cn("flex items-center", { "space-x-2 opacity-70": !user?.isPremium })}>
                                {!user?.isPremium && <IconLock />}
                                <FileInput
                                    w={"100%"}
                                    disabled={!user?.isPremium}
                                    mt="lg"
                                    label="White repo PGN"
                                    onChange={(file) => {
                                        // convert file to string
                                        const reader = new FileReader();
                                        reader.onload = (e) => {
                                            const text = e.target.result;
                                            console.log(text);
                                            setSettings({
                                                ...settings,
                                                whiteRepo: {
                                                    name: file.name,
                                                    content: text as string,
                                                },
                                            });
                                        };
                                        reader.readAsText(file);
                                    }}
                                    value={{ name: settings.whiteRepo?.name } as File}
                                    rightSection={
                                        settings.whiteRepo ? (
                                            <ActionIcon
                                                variant="subtle"
                                                onClick={() => {
                                                    setSettings({
                                                        ...settings,
                                                        whiteRepo: undefined,
                                                    });
                                                }}
                                            >
                                                <IconTrash />
                                            </ActionIcon>
                                        ) : undefined
                                    }
                                />
                            </div>
                            <div className={cn("flex items-center", { "space-x-2 opacity-70": !user?.isPremium })}>
                                {!user?.isPremium && <IconLock />}
                                <FileInput
                                    w={"100%"}
                                    disabled={!user?.isPremium}
                                    mt="lg"
                                    label="Black repo PGN"
                                    onChange={(file) => {
                                        // convert file to string
                                        const reader = new FileReader();
                                        reader.onload = (e) => {
                                            const text = e.target.result;
                                            console.log(text);
                                            setSettings({
                                                ...settings,
                                                blackRepo: {
                                                    name: file.name,
                                                    content: text as string,
                                                },
                                            });
                                        };
                                        reader.readAsText(file);
                                    }}
                                    value={{ name: settings.blackRepo?.name } as File}
                                    rightSection={
                                        settings.blackRepo ? (
                                            <ActionIcon
                                                variant="subtle"
                                                onClick={() => {
                                                    setSettings({
                                                        ...settings,
                                                        blackRepo: undefined,
                                                    });
                                                }}
                                            >
                                                <IconTrash />
                                            </ActionIcon>
                                        ) : undefined
                                    }
                                />
                            </div>
                        </div>
                    )}
                </Tabs.Panel>

                <Tabs.Panel pt="xl" value="experimental">
                    {/* <Space h="xl" /> */}

                    {/* <Switch
                        label="Display Stockfish info"
                        description="Will display evaluations and time taken"
                        checked={settings.useLocalStockfish}
                        onChange={(e) =>
                            setSettings({
                                ...settings,
                                useLocalStockfish: e.currentTarget.checked,
                            })
                        }
                    /> */}

                    {/* <Select
                        className="ml-auto"
                        disabled={!settings.useLocalStockfish}
                        label="Stockfish depth"
                        value={settings.stockfishDepth.toString()}
                        data={["16", "17", "18", "19", "20", "21", "22", "23", "24", "25"]}
                        allowDeselect={false}
                        onChange={(option) => {
                            setSettings({ ...settings, stockfishDepth: Number(option) });
                        }}
                    /> */}

                    <Switch
                        label="Use external API for evaluations"
                        description="Local Stockfish is used by default, but you can server evaluations"
                        checked={settings.useLocalStockfish}
                        mt="xl"
                        onChange={(e) =>
                            setSettings({
                                ...settings,
                                useLocalStockfish: e.currentTarget.checked,
                            })
                        }
                    />

                    {/* <Divider my="lg" /> */}
                </Tabs.Panel>
            </Tabs>
            <div>
                <div className="flex justify-center mt-12">
                    <Button
                        onClick={() => {
                            if (!settings.allowMultipleLevels && levels.length > 1) {
                                setLevels([levels[0]]);
                            }
                            setSettings({
                                ...settings,
                                startRating,
                                gameDuration,
                            });
                            setOpenedSettings(false);
                        }}
                        fullWidth
                        h="3rem"
                    >
                        Save
                    </Button>
                </div>
                <div className="flex justify-center mt-4">
                    <Button variant="outline" onClick={handleResetToDefault} fullWidth>
                        Reset to default
                    </Button>
                </div>

                {!canWinTrophies && (
                    <div className="flex space-x-2 mt-8">
                        <IconInfoTriangle />
                        <Text size="sm" c="dimmed">
                            Rated mode is deactivated with these settings
                        </Text>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SettingsModal;
